import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Select } from './ui/Select';
interface TripInquiryModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripTitle?: string;
  tripDuration?: string;
}
export function TripInquiryModal({
  isOpen,
  onClose,
  tripTitle,
  tripDuration
}: TripInquiryModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      // Analytics event would fire here
      console.log('Analytics: trip_inquiry_submitted', {
        trip: tripTitle
      });
      // Close modal after 2 seconds
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
      }, 2000);
    }, 1500);
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-224">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" data-id="element-225" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-226">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          duration: 0.5
        }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto pointer-events-auto" data-id="element-227">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl" data-id="element-228">
                <div data-id="element-229">
                  <h3 className="text-2xl font-serif font-bold text-primary" data-id="element-230">
                    Request Trip Information
                  </h3>
                  {tripTitle && <p className="text-sm text-teal font-medium mt-1" data-id="element-231">
                      {tripTitle} {tripDuration && `• ${tripDuration}`}
                    </p>}
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full" aria-label="Close modal" data-id="element-232">
                  <X size={24} data-id="element-233" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6" data-id="element-234">
                {isSuccess ? <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} className="text-center py-8" data-id="element-235">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-236">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-237">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-238" />
                      </svg>
                    </div>
                    <h4 className="text-xl font-bold text-primary mb-2" data-id="element-239">
                      Request Received!
                    </h4>
                    <p className="text-gray-600" data-id="element-240">
                      We'll send you detailed information about this trip within
                      24 hours.
                    </p>
                  </motion.div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-241">
                    <p className="text-gray-600 text-sm mb-6" data-id="element-242">
                      Fill out the form below and we'll send you a detailed
                      itinerary, pricing, and availability for this trip.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-243">
                      <Input label="First Name" placeholder="John" required data-id="element-244" />
                      <Input label="Last Name" placeholder="Smith" required data-id="element-245" />
                    </div>

                    <Input label="Email Address" type="email" placeholder="john.smith@example.com" required data-id="element-246" />

                    <Input label="Phone Number" type="tel" placeholder="(555) 123-4567" required data-id="element-247" />

                    <Select label="Organization Type" placeholder="Select your group type..." required options={[{
                value: 'faith',
                label: 'Faith Community / Church'
              }, {
                value: 'alumni',
                label: 'Alumni Association'
              }, {
                value: 'senior',
                label: 'Senior Center / Active Adults'
              }, {
                value: 'club',
                label: 'Club / Social Organization'
              }, {
                value: 'corporate',
                label: 'Corporate / Incentive'
              }, {
                value: 'other',
                label: 'Other'
              }]} data-id="element-248" />

                    <Select label="Estimated Group Size" placeholder="Select size..." required options={[{
                value: '10-20',
                label: '10-20 Travelers'
              }, {
                value: '20-30',
                label: '20-30 Travelers'
              }, {
                value: '30-40',
                label: '30-40 Travelers'
              }, {
                value: '40-50',
                label: '40-50 Travelers'
              }, {
                value: '50+',
                label: '50+ Travelers'
              }]} data-id="element-249" />

                    <Select label="Preferred Travel Timeframe" placeholder="When would you like to travel?" required options={[{
                value: 'spring2026',
                label: 'Spring 2026'
              }, {
                value: 'summer2026',
                label: 'Summer 2026'
              }, {
                value: 'fall2026',
                label: 'Fall 2026'
              }, {
                value: 'winter2026',
                label: 'Winter 2026-27'
              }, {
                value: 'flexible',
                label: 'Flexible Dates'
              }]} data-id="element-250" />

                    <div className="pt-4 flex gap-3" data-id="element-251">
                      <Button type="button" variant="outline" fullWidth onClick={onClose} data-id="element-252">
                        Cancel
                      </Button>
                      <Button type="submit" fullWidth disabled={isSubmitting} data-id="element-253">
                        {isSubmitting ? 'Sending...' : 'Send Request'}
                      </Button>
                    </div>

                    <p className="text-xs text-gray-500 text-center pt-2" data-id="element-254">
                      We'll respond within 1 business day with detailed trip
                      information.
                    </p>
                  </form>}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}